import Api from "../../../../../../../../core/services/api/api";
import Utils from "../../../../../../../../core/services/utils/utils";
import {SUPPORT_PREDEFINED_MULTISELECT_TECHNICAL_IMPORT} from "../shared/import-feature-flags";
import {buildMappedPropertyFieldKeys} from "../shared/technical-import-helpers";
import {PreventionInvalidityCode} from "../preventative-interactions";
import {resolvePredefinedPropertyValues} from "./shared/resolve-predefined-property-values";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

const propertyFieldKey = (propertyId) => `property_${propertyId}`;

/**
 * Part technical information import — phased pipeline (see {@link BaseImportTransformer} run contract).
 */
export class PartTechnicalInformationImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     * @param {string|number} industryId
     * @param {Array<*>} [properties] When null/undefined, properties are loaded from the API.
     * @param {{ mappingFields?: Array<{name: string}> }} [options]
     */
    async run(mappedData, isFinalRun = false, industryId, properties = null, options = {}) {
        const rows = mappedData ?? [];
        const mappedPropertyFieldKeys = buildMappedPropertyFieldKeys(options.mappingFields);

        /** @type {{index: number, item: *, partNoUpper: string, codes: string[], predefinedOptionsMeta: Record<string, *>, multiselectNotSupportedMeta: Record<string, *>, missingMappedPropertiesMeta: Record<string, *>}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item: item ? {...item} : item,
            partNoUpper: toPartNumberUpper(item?.partNumber),
            codes: [],
            predefinedOptionsMeta: {},
            multiselectNotSupportedMeta: {},
            missingMappedPropertiesMeta: {},
        }));

        const preventativeIssues = [];

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        const uniqueParts = [...new Set(rowState.filter((r) => r.partNoUpper).map((r) => r.partNoUpper))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
        }

        let technicalProperties = properties;
        if (!technicalProperties) {
            const propertiesResponse = await Api.getEligibleIndustryPropertiesByIndustryId(industryId);
            technicalProperties = propertiesResponse?.data ?? [];
        }

        const predefinedProperties = (technicalProperties ?? []).filter(
            (prop) => prop?.config?.hasPreDefinedValues ?? prop?.hasPreDefinedValues ?? false,
        );

        const predefinedPropertyIds = predefinedProperties.map((p) => p.id);
        /** @type {Record<string, Array<{id: *, title: string}>>} */
        let propertyOptionsByPropertyId = {};
        if (predefinedPropertyIds.length > 0) {
            propertyOptionsByPropertyId = await this.ctx.fetchIndustryPropertyOptionsByPropertyIds(predefinedPropertyIds);
        }

        for (const rs of rowState) {
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        /** @type {Map<number, Array<{industryPropertyId: *, value: *}>>} */
        const propertyValuesByRowIndex = new Map();
        const skippedFieldsByRow = this.ctx.getSkippedFieldsByRow?.() ?? new Map();
        /** @type {number[]} */
        const obsoleteRowIndices = [];

        for (const rs of rowState) {
            if (!rs.item) continue;

            const rowSkippedFields = skippedFieldsByRow.get(rs.index) ?? new Set();
            const technicalInformation = [];

            for (const property of technicalProperties ?? []) {
                const fieldKey = propertyFieldKey(property.id);
                if (!mappedPropertyFieldKeys.has(fieldKey)) {
                    continue;
                }
                if (rowSkippedFields.has(fieldKey)) {
                    continue;
                }

                const hasPredefined = property?.config?.hasPreDefinedValues ?? property?.hasPreDefinedValues ?? false;
                const isMultiSelect = property?.config?.isMultiSelect ?? property?.isMultiSelect ?? false;
                const rawValue = rs.item[fieldKey]?.toString()?.trim() ?? "";

                if (!rawValue.length) {
                    if (!rs.codes.includes(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS)) {
                        rs.codes.push(PreventionInvalidityCode.MISSING_REQUIRED_FIELDS);
                    }
                    rs.missingMappedPropertiesMeta[fieldKey] = {
                        fieldKey,
                        propertyId: property.id,
                        propertyTitle: property.title,
                        hasPredefined,
                        isMultiSelect,
                        industryPropertyTypeId: property.industryPropertyTypeId,
                    };
                    continue;
                }

                if (hasPredefined) {
                    if (isMultiSelect && !SUPPORT_PREDEFINED_MULTISELECT_TECHNICAL_IMPORT) {
                        if (!rs.codes.includes(PreventionInvalidityCode.MULTISELECT_NOT_SUPPORTED)) {
                            rs.codes.push(PreventionInvalidityCode.MULTISELECT_NOT_SUPPORTED);
                        }
                        rs.multiselectNotSupportedMeta[fieldKey] = {
                            fieldKey,
                            propertyId: property.id,
                            propertyTitle: property.title,
                            rawValue,
                        };
                        continue;
                    }

                    const options = propertyOptionsByPropertyId[String(property.id)] ?? [];
                    const resolution = resolvePredefinedPropertyValues(rawValue, options, {isMultiSelect});

                    if (!resolution.resolved) {
                        if (!rs.codes.includes(PreventionInvalidityCode.INVALID_PREDEFINED_OPTION)) {
                            rs.codes.push(PreventionInvalidityCode.INVALID_PREDEFINED_OPTION);
                        }
                        rs.predefinedOptionsMeta[fieldKey] = {
                            fieldKey,
                            propertyId: property.id,
                            propertyTitle: property.title,
                            isMultiSelect,
                            rawValue,
                            unresolvedTokens: resolution.unresolvedTokens,
                        };
                        continue;
                    }

                    if (resolution.imputedDisplay !== undefined && resolution.imputedDisplay !== rawValue) {
                        rs.item[fieldKey] = resolution.imputedDisplay;
                    }

                    if (resolution.apiValue !== undefined) {
                        technicalInformation.push({
                            industryPropertyId: property.id,
                            value: resolution.apiValue,
                        });
                    }
                    continue;
                }

                const parsedValue = Utils.parseTechnicalPropertyValue(property.industryPropertyTypeId, rawValue);
                if (parsedValue === null || parsedValue === undefined) {
                    continue;
                }
                technicalInformation.push({
                    industryPropertyId: property.id,
                    value: parsedValue,
                });
            }

            if (technicalInformation.length > 0) {
                propertyValuesByRowIndex.set(rs.index, technicalInformation);
            }

            if (Object.keys(rs.missingMappedPropertiesMeta).length > 0) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rs.item,
                    invalidityCodes: [PreventionInvalidityCode.MISSING_REQUIRED_FIELDS],
                    meta: {missingMappedProperties: rs.missingMappedPropertiesMeta},
                });
            }

            if (Object.keys(rs.predefinedOptionsMeta).length > 0) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rs.item,
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PREDEFINED_OPTION],
                    meta: {predefinedOptions: rs.predefinedOptionsMeta},
                });
            }

            if (Object.keys(rs.multiselectNotSupportedMeta).length > 0) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rs.item,
                    invalidityCodes: [PreventionInvalidityCode.MULTISELECT_NOT_SUPPORTED],
                    meta: {multiselectNotSupported: rs.multiselectNotSupportedMeta},
                });
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }

            const mergeableCodes = [...new Set(rs.codes)].filter(
                (c) =>
                    c !== PreventionInvalidityCode.INVALID_PREDEFINED_OPTION &&
                    c !== PreventionInvalidityCode.MULTISELECT_NOT_SUPPORTED &&
                    c !== PreventionInvalidityCode.MISSING_REQUIRED_FIELDS,
            );

            if (mergeableCodes.length) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rs.item,
                    invalidityCodes: mergeableCodes,
                });
            }
        }

        // Missing mapped property values are soft issues: they appear in the preventative
        // dialog but do not block import when the row has at least one importable property value.
        const issueIndexSet = new Set(
            preventativeIssues
                .filter((p) =>
                    p.invalidityCodes.some(
                        (c) =>
                            c === PreventionInvalidityCode.INVALID_PART ||
                            c === PreventionInvalidityCode.INVALID_PREDEFINED_OPTION ||
                            c === PreventionInvalidityCode.MULTISELECT_NOT_SUPPORTED,
                    ),
                )
                .map((p) => p.rowIndex),
        );

        const apiRequests = rows.map((item, rowIndex) => {
            if (!item) return null;
            const rs = rowState[rowIndex];
            const partId = partIds[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(rowIndex);
            const propertyValues = propertyValuesByRowIndex.get(rowIndex) ?? [];

            if (partId && !hasIssue && propertyValues.length === 0) {
                obsoleteRowIndices.push(rowIndex);
            }

            if (!partId || hasIssue || propertyValues.length === 0) {
                return isFinalRun ? null : {
                    partId: partId ?? null,
                    partNumber: rs.item?.partNumber,
                    propertyValues,
                };
            }

            return {partId, propertyValues};
        });

        return {apiRequests, preventativeIssues, propertyOptionsByPropertyId, obsoleteRowIndices};
    }
}
